/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDeviceIntegrationProvidersRequest,
  ListDeviceIntegrationProvidersSecurity,
} from "../models/operations/listdeviceintegrationproviders.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDeviceIntegrationProvidersQuery,
  DeviceIntegrationProvidersQueryData,
  prefetchDeviceIntegrationProviders,
  queryKeyDeviceIntegrationProviders,
} from "./deviceIntegrationProviders.core.js";
export {
  buildDeviceIntegrationProvidersQuery,
  type DeviceIntegrationProvidersQueryData,
  prefetchDeviceIntegrationProviders,
  queryKeyDeviceIntegrationProviders,
};

export type DeviceIntegrationProvidersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listProviders deviceIntegrations
 *
 * @remarks
 * List the providers the device integrations framework supports, including the credential fields each needs.
 */
export function useDeviceIntegrationProviders(
  request?: ListDeviceIntegrationProvidersRequest | undefined,
  security?: ListDeviceIntegrationProvidersSecurity | undefined,
  options?: QueryHookOptions<
    DeviceIntegrationProvidersQueryData,
    DeviceIntegrationProvidersQueryError
  >,
): UseQueryResult<
  DeviceIntegrationProvidersQueryData,
  DeviceIntegrationProvidersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDeviceIntegrationProvidersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listProviders deviceIntegrations
 *
 * @remarks
 * List the providers the device integrations framework supports, including the credential fields each needs.
 */
export function useDeviceIntegrationProvidersSuspense(
  request?: ListDeviceIntegrationProvidersRequest | undefined,
  security?: ListDeviceIntegrationProvidersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DeviceIntegrationProvidersQueryData,
    DeviceIntegrationProvidersQueryError
  >,
): UseSuspenseQueryResult<
  DeviceIntegrationProvidersQueryData,
  DeviceIntegrationProvidersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDeviceIntegrationProvidersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDeviceIntegrationProvidersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DeviceIntegrationProvidersQueryData,
): DeviceIntegrationProvidersQueryData | undefined {
  const key = queryKeyDeviceIntegrationProviders(...queryKeyBase);

  return client.setQueryData<DeviceIntegrationProvidersQueryData>(key, data);
}

export function invalidateDeviceIntegrationProviders(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "deviceIntegrations",
      "listProviders",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDeviceIntegrationProviders(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deviceIntegrations", "listProviders"],
  });
}
